'use client';

import React, { useState } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Modal } from '@depop/web-ui-kit/Modal';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { RecentFeedbackListItem } from '@/app/[locale]/products/[slug]/components/RecentReviews/RecentFeedbackListItem';
import styles from '@/app/[locale]/products/[slug]/components/RecentReviews/styles.module.css';
import { FeedbackModal } from '@/components/FeedbackModal';
import { RQ_FEEDBACK_SELLER } from '@/modules/ReactQuery/cacheKeys.ts';
import { fetchFeedback } from '@/modules/feedback/api.ts';
import { FeedbackRole } from '@/modules/feedback/types.ts';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';

const MIN_FEEDBACK_COUNT = 2;
const VISIBLE_FEEDBACK_COUNT = 3;

export function RecentReviews() {
  const tProduct = useTranslations('product');
  const tCommon = useTranslations('common');
  const { isMaxMedium } = useResponsive();
  const isDesktop = !isMaxMedium;
  const { data: productBasic } = useProductBySlugBasicPDP();

  const { data: feedbackData } = useInfiniteQuery({
    queryKey: [RQ_FEEDBACK_SELLER, productBasic?.seller.id],
    queryFn: ({ pageParam }) => {
      if (productBasic) {
        return fetchFeedback({
          role: FeedbackRole.Seller,
          sellerId: productBasic.seller.id,
          offset: pageParam,
          limit: VISIBLE_FEEDBACK_COUNT,
        });
      }
    },
    enabled: !!productBasic && isDesktop,
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage?.data?.page_info?.last,
  });

  const sellerFeedback =
    feedbackData?.pages
      ?.flatMap((page) => page?.data?.objects)
      .filter((item) => !!item) || [];

  const [isModalOpen, setModalOpen] = useState(false);

  function handleSeeMoreClick() {
    setModalOpen(true);
  }

  const hasMinFeedbackRequirement = sellerFeedback
    ? MIN_FEEDBACK_COUNT <= sellerFeedback.length
    : false;

  if (!productBasic || !hasMinFeedbackRequirement) {
    return <></>;
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headingRow}>
          <Heading variant="md">{tProduct('RecentReviews')}</Heading>
          <button className={styles.seeMoreButton} onClick={handleSeeMoreClick}>
            <Body variant="sm-regular" as="span">
              {tCommon('SeeMore')}
            </Body>
          </button>
        </div>

        <ul className={styles.feedbackList}>
          {sellerFeedback.slice(0, VISIBLE_FEEDBACK_COUNT).map((item) => (
            <RecentFeedbackListItem key={item.transaction_id} item={item} />
          ))}
        </ul>
      </div>

      <Modal
        wide
        isOpen={isModalOpen}
        setOpen={setModalOpen}
        title={tCommon('FeedbackFor', {
          username: productBasic.seller.username,
        })}
        content={() => (
          <FeedbackModal
            sellerId={productBasic.seller.id}
            sellerUsername={productBasic.seller.username}
          />
        )}
      />
    </>
  );
}
